import { addFieldVersion, applyFieldVersion, removeFieldVersion, writeFieldVersion } from './versions.js';
import { resolveScenarioValueSetup } from './scenario.js';
import { answerToleranceMode, answerHalfWidths } from './answer-tolerance.js';
import { matchesValueTrigger, parseValueTrigger, triggerToBoundedInterval } from './value-trigger.js';
import { coverageSpansDomain, isIntervalFullyCovered, mergeCoverageIntervals, rangesOverlap } from './interval-coverage.js';
import { optionalOutcome, participationOutcome, SET_ANSWER_TRIGGER } from './grading-mode.js';
import { parse, FunctionNode, SymbolNode, OperatorNode } from 'mathjs';
import { EPS, cleanComputed, valuesMatchWithinEpsilon } from './epsilon.js';
import { angleModeFor, buildFormulaScope, formulaRefKey, preprocessFormula, ANGLE_FUNCTION_NAMES } from './formula-scope.js';
/**
 * IS AN UNCERTAINTY PART OF THIS QUESTION — the one place that decides.
 *
 * Three facts, and every surface needs all three: the teacher asked for one, decimals are
 * allowed, and the question has a single value to be uncertain ABOUT. It was written out
 * inline in fourteen places across five files and had drifted into three different rules,
 * which is the shape this codebase keeps finding bugs in.
 *
 * ## `=` ONLY, which is the part the inline copies did not have
 *
 * An uncertainty is a second answer the student states, and `9.8 ± 0.3` only means anything
 * against a single value. "Less than 5 ± 0.3" is not a question, and "between 3 and 7 ± 0.3"
 * states the band twice in two notations that need not agree. The owner's call when asked
 * whether to restrict it: *"i think keep it = only."*
 *
 * The flag is KEPT, not cleared, when the condition changes — the editor greys the toggle
 * rather than removing it, and a teacher who switches to `between` and back finds their
 * uncertainty where they left it. So the stored flag is never the answer on its own, and
 * nothing may read `uncertaintyRequired` without also asking this.
 *
 * `conditionType` defaults to `exact` because that is the block's own default and data
 * written before the field existed is an exact-answer block.
 */
export function uncertaintyAsked(d) {
    if (!d.uncertaintyRequired || d.integerOnly)
        return false;
    return (d.conditionType ?? 'exact') === 'exact';
}
export const MATH_ERROR_CATEGORIES = [
    'sign_flip', 'dropped_parens', 'exponent_misread',
    'swapped_operands', 'ignored_precedence',
    'wrong_angle_mode', 'log_ln_confusion',
];
/**
 * Counts significant figures from a raw submitted number string, following the
 * trailing-zero convention: "10" = 1 sig fig, "10." = 2 sig figs, "10.0" = 3 sig figs.
 * Leading zeros are never significant; trailing zeros are only significant when a
 * decimal point is present, since that's the only way to signal deliberate precision.
 */
/**
 * Is `raw` written in normalized scientific notation?
 *
 * Normalized is the form science teaching means by "scientific notation": exactly one non-zero
 * digit before the decimal point. `14e1` and `0.14e3` are the same NUMBER as `1.4e2` and are not
 * the same ANSWER when the notation is what is being assessed.
 *
 * Zero is the one value with no normalized form (a mantissa of 0 cannot satisfy 1 <= |m| < 10),
 * so it is accepted bare or with any exponent. Without that carve-out a block whose answer is
 * zero would be unanswerable.
 */
/** This block's own reading of a trigger as coverage — `exact` adds none (a point never closes a
 *  gap). Injected into the shared merge so the interval maths is shared and the trigger vocabulary
 *  stays here. */
function numericalTriggerSpan(trigger) {
    if (parseValueTrigger(trigger)?.type === 'exact')
        return null;
    return triggerToBoundedInterval(trigger);
}
export function isScientificNotation(raw) {
    const s = raw.trim();
    // Zero is checked before the exponent is even looked for: `0` is as normalized as zero gets,
    // and demanding `0e0` of a student would be requiring a form no one writes.
    const asNumber = parseFloat(s);
    if (Number.isFinite(asNumber) && asNumber === 0)
        return true;
    const at = s.search(/[eE]/);
    if (at === -1)
        return false;
    const mantissa = s.slice(0, at);
    const exponent = s.slice(at + 1);
    if (!/^[+-]?\d+$/.test(exponent))
        return false;
    const m = parseFloat(mantissa);
    if (!Number.isFinite(m))
        return false;
    const abs = Math.abs(m);
    return abs >= 1 && abs < 10;
}
export function countSigFigs(raw) {
    let s = raw.trim().replace(/^[+-]/, '');
    const eIdx = s.search(/[eE]/);
    if (eIdx !== -1)
        s = s.slice(0, eIdx);
    if (!s)
        return 0;
    const hasDecimal = s.includes('.');
    const [intPart = '', fracPart = ''] = s.split('.');
    if (!hasDecimal) {
        const noLeadingZeros = intPart.replace(/^0+/, '');
        if (!noLeadingZeros)
            return 0; // "0" or "" — nothing significant
        const noTrailingZeros = noLeadingZeros.replace(/0+$/, '');
        return noTrailingZeros.length || 1; // "100" → "1" (1 sig fig), never 0 once a nonzero digit exists
    }
    const combined = (intPart + fracPart).replace(/^0+/, '');
    return combined.length;
}
/**
 * Can any number written with exactly `sigFigs` significant figures land inside this band?
 *
 * The question a teacher never thinks to ask, and the block never asked either. Set an answer of
 * 106, then switch on "2 significant figures", and no submission can be right: 106 rounded to two
 * figures is 110, which is outside a zero-tolerance band, while 106 itself carries three figures and
 * the format fault becomes the verdict. The student who FOLLOWS the instruction is marked plainly
 * incorrect; the one who ignores it is told about the formatting. Nobody is ever marked correct.
 *
 * The numbers with exactly N significant figures form a lattice whose spacing changes at each power
 * of ten — 2 figures gives ...98, 99, 100, 110, 120... — so the test is whether any lattice point
 * falls in the band, not whether the answer itself is expressible. A tolerance usually absorbs the
 * rounding, which is why this is a real check and not a syntactic one.
 *
 * Answers true wherever it cannot be sure, which is the same direction every other claim in this
 * file declines in. An unbounded side always has room; a band touching or crossing zero has room on
 * at least one side, since the lattice grows arbitrarily fine approaching zero; and an inverted band
 * is already its own error and does not need a second one piled on.
 */
export function bandAdmitsSigFigs(lo, hi, sigFigs) {
    if (!Number.isFinite(lo) || !Number.isFinite(hi))
        return true;
    if (lo > hi || sigFigs < 1)
        return true;
    if (lo <= 0 && hi >= 0)
        return true;
    // Mirror a wholly negative band onto the positives; the lattice is symmetric about zero.
    const [a, b] = lo > 0 ? [lo, hi] : [-hi, -lo];
    /**
     * Only the LOWER bound's decade is consulted, and that is sufficient rather than sloppy.
     *
     * Spacing grows with magnitude, so `a`'s step is always the finer of the two and its first
     * lattice point at or above `a` is the closest one there is. Checking `b`'s decade as well was
     * tried and cannot ever help — a mutation removing it killed no test, which is how the redundancy
     * surfaced, and the comment defending it had the reasoning backwards.
     *
     * The lattice continues past a power of ten because scientific notation carries the figures a
     * plain number loses: 100 is one significant figure written as `100` and two written as `1.0e2`,
     * and `countSigFigs` reads the mantissa.
     */
    const step = Math.pow(10, Math.floor(Math.log10(a)) - sigFigs + 1);
    // A hair of slack, so a lattice point sitting exactly on a computed boundary counts as inside
    // rather than being lost to the last bit of the division.
    const first = Math.ceil(cleanComputed(a / step) - 1e-9) * step;
    return cleanComputed(first) <= b + EPS(b);
}
/**
 * ═══ PRECISION AGREEMENT ═══════════════════════════════════════════════════════════════════════
 *
 * `9.8 ± 0.3` is right. `9.81234 ± 0.3` is wrong — the uncertainty says the digits past the first
 * decimal carry no information, so writing them claims a precision the student has just disclaimed.
 * This is the rule that separates someone who understands uncertainty from someone copying a
 * calculator display.
 *
 * THE RULE IS DECIMAL PLACE, NEVER SIG-FIG COUNT. `1230 ± 50` agrees and the two numbers carry a
 * different number of significant figures (3 and 1); what they share is where their last
 * significant digit SITS. Reading it as a count gets `1204` and `1234` different answers for the
 * same question, which is how you can tell the count is the wrong quantity.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * The power of ten the last significant digit sits at, or null when there is no significant digit
 * at all (blank, zero, unparseable).
 *
 * DELIBERATELY NOT MODELLED ON `countSigFigs`, which strips everything from the `e` onward. A
 * sig-fig count is exponent-independent — `9.8` and `9.8e3` both carry two — but a PLACE is not:
 * the same digits sit at the tenths in one and the hundreds in the other. So the exponent is added
 * here rather than discarded, which is the whole of what scientific notation costs this rule.
 *
 *   9.8 -> -1      9.8e3 -> 2       1e2 -> 2
 *   450 ->  1      450.  -> 0       0.0030 -> -4
 *
 * A ZERO HAS NO SIGNIFICANT FIGURES AND STILL HAS A PRECISION, which is the one case a figure
 * count cannot express at all. `0.00 ± 0.05` is an ordinary null result — no net force, to within
 * 0.05 — and `0.00` is unambiguously written to the hundredths. So a zero gets its written place
 * like any other number, and only the digits that are ABSENT are read, never their value.
 *
 * Whether a zero is a usable UNCERTAINTY is a different question with a different answer, and it
 * belongs to `precisionDisagrees` rather than here. Conflating the two is what made `0.0 ± 0.05`
 * silently fine.
 */
export function lastSignificantPlace(raw) {
    const s = raw.trim().replace(/^[+-]/, '');
    if (!s)
        return null;
    const eIdx = s.search(/[eE]/);
    const mantissa = eIdx === -1 ? s : s.slice(0, eIdx);
    const expPart = eIdx === -1 ? '0' : s.slice(eIdx + 1);
    if (!/^[+-]?\d+$/.test(expPart))
        return null;
    if (!/^\d*\.?\d*$/.test(mantissa))
        return null;
    if (!/\d/.test(mantissa))
        return null;
    const exp = parseInt(expPart, 10);
    const [intPart = '', fracPart = ''] = mantissa.split('.');
    if (mantissa.includes('.'))
        return -fracPart.length + exp;
    // `0` is written to the units place; stripping its trailing zeros leaves nothing to count from,
    // so the all-zero integer is the one case the trailing-zero rule cannot answer on its own.
    if (!/[1-9]/.test(intPart))
        return exp;
    const trailingZeros = intPart.length - intPart.replace(/0+$/, '').length;
    return trailingZeros + exp;
}
/**
 * The places a written number could PLAUSIBLY be carrying, tightest to loosest.
 *
 * The same ambiguity `maxSigFigs` exists for: `450` is 2 significant figures by the trailing-zero
 * convention and 3 if the student simply did not write the point, and nobody can tell. A decimal
 * point removes it (`450.` is exactly the units place), and so does scientific notation — which is
 * precisely what `requireExponentialNotation` is for.
 */
function placeSpan(raw) {
    const max = lastSignificantPlace(raw);
    if (max === null)
        return null;
    const s = raw.trim().replace(/^[+-]/, '');
    const eIdx = s.search(/[eE]/);
    const mantissa = eIdx === -1 ? s : s.slice(0, eIdx);
    if (mantissa.includes('.'))
        return { min: max, max };
    /*
     * A BARE ZERO PINS NO PLACE AT ALL — the extreme of the same ambiguity, open-ended upward.
     *
     * `450` could be carrying the tens or the units. `0` has no significant digit to carry anything,
     * so it could be a zero to the nearest unit, ten or thousand, and nothing in the string decides.
     * That matters because `0 ± 10` is how a null result consistent with zero is actually written,
     * and reading the `0` at the units would flag the one notation a physicist would reach for.
     *
     * `0.0` is a different claim: the point pins it, and the branch above has already taken it.
     */
    if (!/[1-9]/.test(mantissa))
        return { min: max, max: Infinity };
    const trailingZeros = mantissa.length - mantissa.replace(/0+$/, '').length;
    return { min: max - trailingZeros, max };
}
/**
 * Do the two numbers the student wrote disagree about precision?
 *
 * ASYMMETRIC, and that is the point. The VALUE gets its ambiguity span, because its trailing zeros
 * are genuinely unreadable and the standing rule is to stay quiet where no reading could be called
 * wrong. The UNCERTAINTY gets a single place, because its significant figures are not ambiguous —
 * they are governed by `uncertaintySigFigs`, which the teacher set.
 *
 * Giving the uncertainty a span too was tried and is wrong: it makes `1234 ± 50` agree, by reading
 * the `50` as carrying a units digit nobody writes. The correctly reported form is `1230 ± 50`, and
 * a rule that cannot tell those apart has given up on the only thing it was asked to check.
 */
export function precisionDisagrees(value, uncertainty) {
    /*
     * A ZERO UNCERTAINTY MAKES NO PRECISION CLAIM to agree or disagree with. `± 0` asserts infinite
     * precision, which is not a thing measurement reports, and the correctness check already says so
     * in the one place it needs saying — piling a second verdict on top names the same mistake twice.
     *
     * Note this asks the VALUE, not the place: `0.00` is a precision (the hundredths) while `± 0` is
     * a quantity, and only the latter is disqualifying.
     */
    const magnitude = parseFloat(uncertainty);
    if (!Number.isFinite(magnitude) || magnitude <= 0)
        return false;
    const u = lastSignificantPlace(uncertainty);
    if (u === null)
        return false;
    const span = placeSpan(value);
    if (span === null)
        return false;
    return u < span.min || u > span.max;
}
/**
 * A BAND EDGE ROUNDED THE WAY A BAND EDGE HAS TO BE: never outward.
 *
 * A displayed bound is a promise about which submissions are accepted, so rounding it to the
 * precision the question demands must never widen it. `9.7934` shown to four significant figures
 * is `9.793` — and `9.793` is BELOW the real edge, so the panel advertised a value the grader
 * rejects, while the Incorrect chip beside it said `< 9.793`. Both chips described the same edge
 * and contradicted each other about the one number sitting on it.
 *
 * Rounding inward instead gives `9.794`, which is the smallest four-figure value actually
 * accepted, and the two chips agree again. The band shown is a subset of the band graded.
 *
 * `dir` is which side of the band this is — `up` for a lower bound, `down` for an upper one.
 */
export function boundAtSigFigs(n, sigFigs, dir) {
    if (!Number.isFinite(n) || n === 0 || sigFigs < 1)
        return n;
    const step = Math.pow(10, Math.floor(Math.log10(Math.abs(n))) - sigFigs + 1);
    // A hair of slack so a bound already ON the lattice is not nudged a whole step by float noise.
    const q = cleanComputed(n / step);
    const snapped = (dir === 'up' ? Math.ceil(q - 1e-9) : Math.floor(q + 1e-9)) * step;
    return cleanComputed(snapped);
}
/**
 * Can any number written to this DECIMAL PLACE land inside the band?
 *
 * The companion to `bandAdmitsSigFigs`, and needed for the same reason one place further on. With
 * an error box the value's precision is not a figure COUNT the teacher set — it is the place the
 * student's uncertainty dictates, so the lattice is evenly spaced at 10^p rather than widening at
 * each power of ten. `9.8134 ± 0.005` is [9.8084, 9.8184], and a student whose uncertainty says
 * tenths has only `9.8` to offer, which is outside it. Nobody can be right.
 *
 * Answers true wherever it cannot be sure, the direction every other claim in this file declines
 * in: an unbounded side always has room, and an inverted band is already its own error.
 */
export function bandAdmitsPlace(lo, hi, place) {
    if (!Number.isFinite(lo) || !Number.isFinite(hi))
        return true;
    if (lo > hi)
        return true;
    const step = Math.pow(10, place);
    if (!Number.isFinite(step) || step <= 0)
        return true;
    const first = Math.ceil(cleanComputed(lo / step) - 1e-9) * step;
    return cleanComputed(first) <= hi + EPS(hi);
}
/**
 * A number in EXPONENT FORM landing on a given significant place.
 *
 * `9.8e3` has its last significant figure at the hundreds: the exponent is 3, and one mantissa
 * decimal walks it down to 2. So to put a value on a place, give its mantissa exactly
 * `exponent - place` decimals — which is what makes `9.8e3 ± 1e2` agree under
 * `precisionDisagrees`, and what a chip needs to advertise a pair the grader will accept when
 * both scientific notation and an error box are asked for.
 */
export function atPlaceExponential(value, place) {
    if (!Number.isFinite(value) || value === 0)
        return String(value);
    const exponent = Math.floor(Math.log10(Math.abs(value)));
    return value.toExponential(Math.max(0, exponent - place));
}
/**
 * How many decimals an uncertainty of this magnitude is written to, at the figures asked for.
 *
 * From the FIGURE COUNT and the exponent, never from the number's own spelling: `0.10` parsed and
 * re-printed is `0.1`, and that lost trailing zero is the entire difference between one
 * significant figure and two. Survives unit conversion for the same reason — it reads the scaled
 * value's magnitude rather than any string.
 */
export function uncertaintyDecimalsFor(scaled, sigFigs) {
    if (!Number.isFinite(scaled) || scaled === 0)
        return 0;
    return Math.max(0, Math.max(1, sigFigs) - 1 - Math.floor(Math.log10(Math.abs(scaled))));
}
/** `toPrecision` returns a string that pads; `Number` of it does not. See the sig-figs rounding note. */
export function roundToSigFigs(v, sigFigs) {
    if (!Number.isFinite(v) || v === 0 || sigFigs < 1)
        return v;
    return cleanComputed(Number(v.toPrecision(sigFigs)));
}
/**
 * Is the student's uncertainty inside the band the teacher authored?
 *
 * A PLAIN RANGE CHECK, and deliberately nothing more. This used to round the key to the required
 * figures and accept the rounding, on the reasoning that a propagation formula yields 0.2847 while
 * the student is told to write 0.3. That reasoning was wrong in a way worth recording: it took the
 * decision away from the teacher. A tolerance is how a teacher says which neighbouring values they
 * will accept, and rounding on their behalf both overrides that and hides the case where their two
 * settings admit nothing at all.
 *
 * So the uncertainty is graded the way the value beside it is — a number, a band, and a separate
 * question about how it was written. The two checks are independent, and a setup where no number
 * can satisfy both is reported to the teacher rather than quietly resolved (see
 * `bandAdmitsSigFigs`, which the value already uses for exactly this).
 */
export function uncertaintyMatches(submitted, lo, hi) {
    if (!Number.isFinite(submitted) || !Number.isFinite(lo) || !Number.isFinite(hi))
        return false;
    return submitted >= lo - EPS(lo) && submitted <= hi + EPS(hi);
}
/**
 * ONE FIGURE ADMITS ONE VALUE, whatever a tolerance field still holds.
 *
 * The editor only offers the tolerance row at two significant figures, and the reason is the
 * lattice: at one figure the next admissible uncertainty is 20% to 100% away, so the narrowest
 * band a tolerance can express is already wider than any disagreement between propagation
 * methods. The rounding convention is the band.
 *
 * Enforced HERE as well as in the editor, because a teacher who sets two figures, types a
 * tolerance and then drops back to one leaves that number in the data where nothing on screen
 * shows it. A hidden control that still grades is the quiet wrong answer this codebase keeps
 * meeting. The fields are kept rather than cleared, so stepping back up restores what was typed.
 */
export function uncertaintyBand(candidate, d) {
    if ((d.uncertaintySigFigs ?? 1) < 2)
        return { lo: candidate, hi: candidate };
    const bearing = {
        toleranceValue: d.uncertaintyTolerance ?? 0,
        toleranceAbove: d.uncertaintyToleranceAbove ?? 0,
        toleranceBelow: d.uncertaintyToleranceBelow ?? 0,
        toleranceSymmetry: d.uncertaintyToleranceSymmetry,
        toleranceMode: d.uncertaintyToleranceMode,
    };
    const { below, above } = answerHalfWidths(bearing);
    // 'percentage' to match the editor's default, which the answer's tolerance also takes.
    const pct = answerToleranceMode(bearing, undefined, 'percentage') === 'percentage';
    const lo = pct ? candidate * (1 - Math.abs(below) / 100) : candidate - Math.abs(below);
    const hi = pct ? candidate * (1 + Math.abs(above) / 100) : candidate + Math.abs(above);
    return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
}
/**
 * The accepted range(s) implied by the canonical condition, expressed as closed
 * intervals (one-sided conditions use ±Infinity). Multiple exact/between answers are
 * OR'd together — matching any one of them is correct. Used by both the correctness
 * check and the reachability sweep; exact boundary strictness for lt/gt is handled
 * separately in isWithinAcceptedRange since a closed interval can't express "exclusive".
 */
/*
 * An answer that did not RESOLVE contributes no accepted range at all.
 *
 * A half-typed answer row reaches here as NaN, and a NaN band is not merely useless — it is
 * actively corrosive. On a bounded domain `wrap(NaN, NaN)` came out as [NaN, 360] and [0, NaN],
 * two bogus spans that merged into the coverage and SWALLOWED a real gap: an answer of 90 +/- 5
 * with a blank second row reported Default Incorrect as covering only 0-85, losing 95-360
 * entirely. On an unbounded one it produced a gap running to NaN, printed as "greater than NaN".
 *
 * Dropping it at source rather than in each caller, because every consumer wants the same thing
 * and there are eighteen of them. Grading is unaffected: no submitted value ever satisfied a NaN
 * comparison, so these intervals only ever misinformed the EDITOR.
 */
/**
 * How close a submission has to be to a math-error candidate to count as having LANDED on it.
 *
 * This was `EPS`, which is a representation-noise epsilon — about 3.6e-15 near 1, and its own
 * doc says it sits "far below anything a person could type deliberately". Correct for its real
 * job, comparing two values we both computed; wrong here, where one side is a number a human
 * typed. A student confusing log with ln on `log({test})-2` computes 0.3025850929940457 and had
 * to type all sixteen digits to be told so. Reported, and the diagnosis was "they round off".
 *
 * The teacher has already declared how close counts as the same answer — it is the tolerance on
 * the answer itself — so a candidate is matched at that same width. The same principle attempt
 * signatures settled: the block's own tolerance is the unit of "same answer".
 *
 * BETWEEN mode declares no point tolerance, so there is nothing to borrow. There the width comes
 * from the student's own precision: type `0.30` and anything rounding to 0.30 counts, which is
 * the only honest reading of a number written to two decimals.
 *
 * Never narrower than EPS, so representation noise can still never separate two equal values.
 */
function candidateHalfWidth(candidate, config, typed) {
    const answer = config.answers[0];
    if (config.conditionType === 'exact' && answer) {
        const { below, above } = answerHalfWidths(answer, config);
        const declared = Math.max(Math.abs(below), Math.abs(above));
        const width = modeForAnswer(answer, config) === 'absolute'
            ? declared
            : Math.abs(candidate) * (declared / 100);
        return Math.max(width, EPS(candidate));
    }
    const decimals = /\.(\d+)/.exec(typed.trim())?.[1]?.length ?? 0;
    return Math.max(0.5 * Math.pow(10, -decimals), EPS(candidate));
}
/**
 * ABSOLUTE OR PERCENTAGE FOR ONE ANSWER, with integer-only overriding both.
 *
 * `integerOnly` is not a preference the answer can outvote: a percentage of an integer is a
 * fraction, so a band authored as `7 ± 10%` on an integer block accepts 6.3 to 7.7 and grades
 * a student's `6` wrong while showing them a range that contains no other integer.
 *
 * It lives here rather than in the editor because the editor already enforced it once, by
 * forcing the block's mode to absolute whenever the toggle went on — and that invariant broke
 * silently the moment the mode moved onto each answer, where a NEW answer inherits the block's
 * old percentage and the control that would fix it is disabled. Enforcing it at the point of
 * use is the version that cannot come apart again.
 */
function modeForAnswer(answer, config) {
    return config.integerOnly ? 'absolute' : answerToleranceMode(answer, config, 'absolute');
}
export function computeAcceptedRanges(config) {
    const { conditionType, answers, betweenAnswers, threshold } = config;
    if (conditionType === 'exact') {
        return answers.filter(a => Number.isFinite(a.value)).map(a => {
            const { below, above } = answerHalfWidths(a, config);
            const mode = modeForAnswer(a, config);
            /**
             * `Math.abs`, because a tolerance is a DISTANCE and a percentage of a negative answer is
             * not. `log(10)-2` is -1, and 1% of -1 is -0.01, so the band came out
             * `lo: -0.99, hi: -1.01` — inverted, and `isWithinAcceptedRange` then rejected every value
             * including the exact answer. A teacher with a negative answer and percentage error had a
             * block that could not be answered correctly at all.
             *
             * Absolute mode is clamped the same way: a negative distance is meaningless whatever typed
             * it, and letting one through produces the same inverted interval.
             */
            const loErr = Math.abs(mode === 'absolute' ? below : a.value * (below / 100));
            const hiErr = Math.abs(mode === 'absolute' ? above : a.value * (above / 100));
            // A tolerance band includes its own endpoints — "5 ± 1" accepts exactly 4 and exactly 6.
            // Cleaned, not the raw subtraction: `1.15 - 1.15*0.30` is 0.8049999999999999 and
            // `1.15 + 1.15*0.30` is 1.49499999999999988, so both ends of a plain percentage band miss
            // the decimal the teacher had in mind.
            return {
                lo: cleanComputed(a.value - loErr), hi: cleanComputed(a.value + hiErr),
                loInclusive: true, hiInclusive: true,
            };
        });
    }
    if (conditionType === 'between') {
        // Typed literals are already clean; a formula-authored bound (`{a}*1.1`) is not.
        return betweenAnswers.filter(a => Number.isFinite(a.low) && Number.isFinite(a.high))
            .map(a => ({ lo: cleanComputed(a.low), hi: cleanComputed(a.high), loInclusive: true, hiInclusive: true }));
    }
    if (conditionType === 'lt' || conditionType === 'lte') {
        return [{ lo: -Infinity, hi: cleanComputed(threshold), loInclusive: true, hiInclusive: conditionType === 'lte' }];
    }
    return [{ lo: cleanComputed(threshold), hi: Infinity, loInclusive: conditionType === 'gte', hiInclusive: true }];
}
/**
 * True when `value` lies inside `iv`, honouring each end's own open/closed boundary.
 *
 * EXACT — no epsilon on either side. "At most 5.5" means at most 5.5, and 5.500000001 is greater
 * than 5.5, which in a grading context is the answer a teacher expects. The representation noise
 * an epsilon used to absorb is gone before this runs: `computeAcceptedRanges` cleans each bound it
 * computes (see `cleanComputed`), so the interval already holds the decimal the teacher meant.
 */
function containsValue(iv, value) {
    const aboveLo = iv.loInclusive ? value >= iv.lo : value > iv.lo;
    const belowHi = iv.hiInclusive ? value <= iv.hi : value < iv.hi;
    return aboveLo && belowHi;
}
/** Is this canonical-unit value within the accepted condition? Handles lt/gt's exclusive boundary explicitly. */
export function isWithinAcceptedRange(value, config) {
    // EVERY condition type goes through the interval model, thresholds included.
    //
    // The four threshold types used to short-circuit with a bare comparison above this line, which
    // skipped `containsValue` and therefore skipped its epsilon — so "at most 0.3" graded one way as
    // `lte` and the other way as `between 0 and 0.3`, on the same submission. It bit hardest exactly
    // where it was least affordable: `threshold` accepts formulas, so `lte {a}+{b}` produces a
    // float-dirty boundary by construction and a student typing the clean decimal was marked wrong.
    //
    // Nothing is added here to fix it. `computeAcceptedRanges` already models a threshold as a
    // half-open interval carrying its own `loInclusive`/`hiInclusive`, so routing every condition
    // type through one comparison is all it takes for `lte` and `between` to agree.
    //
    // This comment used to claim `containsValue` "applies the epsilon to inclusive ends only". It
    // does not, and has not since numerical moved to cleaning its bounds instead (`cleanComputed`) —
    // the sentence described SLIDER's strategy, which is the right one for slider and the wrong one
    // here. See the note at the foot of `value-trigger.ts` for why the two blocks differ on purpose.
    // Left recorded rather than silently deleted: a confidently wrong comment cost this session real
    // time, and a stale explanation is worse than none.
    return computeAcceptedRanges(config).some(r => containsValue(r, value));
}
/**
 * Returns the feedback states that match a student's answer.
 *
 * Evaluation sequence:
 * 1. optional mode → any_response, answered or not — no completeness or correctness check
 *    at all for this mode (see positiveTriggerForMode in use-input-block-feedback.ts)
 * 2. Empty answer (graded/survey only) → unanswered state (both value and, in withUnits
 *    mode, unit must be empty)
 * 3. Incomplete answer (withUnits mode only) → exactly one of value/unit present, not both —
 *    a structural gate, checked before correctness can even be attempted, same tier as
 *    No Answer. Unreachable when unitMode isn't 'withUnits' (nothing to be "incomplete" about).
 * 4. survey mode → any_answer (any complete, non-incomplete answer counts as participation)
 * 5. graded mode:
 *    a. Unit check (withUnits mode only) — matched by raw text against each unit's own list of
 *       spellings, unitOptions first:
 *       - Matches an entry in unitOptions, by its label or any of its spellings → that entry's
 *         role decides, below. Checked ahead of the canonical because a row IS a spelling the
 *         teacher broke out to say something more specific about.
 *       - Else matches canonicalUnit or one of canonicalSpellings → always correct, value used
 *         as-is, no conversion. The canonical unit is never itself an entry in unitOptions; it's
 *         implicitly role: 'correct' by definition.
 *       - For a matched option: role: 'correct' → convert the
 *         submitted number to canonical terms (factor/offset) and proceed to the value
 *         check on the converted number. role: 'neutral' | 'wrong', or no match at all
 *         (unrecognized text) → terminal: per-unit feedback fires if the teacher authored
 *         some for that unit (only possible when the unit was actually recognized —
 *         there's nothing to key feedback off of for unrecognized text), else Default
 *         Incorrect. Value and sig figs are never evaluated on this path.
 *       Neutral doesn't get a "floats free" exemption the way MCQ multi-select's does:
 *       there's only ever one submitted value, so an unpromoted unit choice can never
 *       coexist with a separately-satisfied correct answer the way a MCQ multi-select's
 *       neutral option can.
 *    b. Value check — canonical-unit value (converted if a correct-role alternative unit
 *       was used, unconverted if canonical or no units) compared against the accepted
 *       condition.
 *       - In range: sig figs required? fails → Sigfig Error (fixed state); passes or not
 *         required → Correct. Sig figs is counted from the raw submitted string (never
 *         the converted value) and is gated behind the value check (not checked first,
 *         unlike units) because it isn't a structural precondition — a value can be
 *         validly compared to the accepted range regardless of its formatting, unlike
 *         units, which have to resolve before any comparison is even possible.
 *       - Out of range: value feedback (exact/lt/lte/gt/gte/range, parallel — all matching
 *         states fire together) fires if any match, else Default Incorrect.
 *
 * Correct-first-terminal (implicit in the branching above): value feedback is only ever
 * consulted once the value is already known to be out of range, so feedback whose range
 * happens to overlap the accepted condition can never override a genuinely correct
 * answer — this matters more here than for MCQ, since numerical feedback conditions are
 * freeform ranges on a continuum with no structural mechanism preventing overlap with Correct.
 *
 * Trigger formats:
 *   unit_X                fires when unit X was selected (a non-correct unit was chosen)
 *   exact:N / lt:N / ...  fires when the canonical-unit value matches that condition
 */
/**
 * The MOST significant figures the digits could be carrying, if every trailing zero counted.
 *
 * `countSigFigs` gives the least — the trailing-zero convention, where `450` is 2. The truth is
 * that `450` is 2 OR 3 and nobody can tell, which is the ambiguity a scientific-notation
 * requirement exists to remove. Reading both ends is what lets the sig-fig fault be reported under
 * that ambiguity when NO reading could satisfy the requirement, and stay quiet when one could.
 */
function maxSigFigs(raw) {
    const digits = raw.trim().replace(/^[-+]/, '').split(/[eE]/)[0].replace('.', '').replace(/^0+/, '');
    return digits.length || 1;
}
/**
 * What is wrong with how the answer was WRITTEN, independent of what it says.
 *
 * Both checks read the raw submitted string and nothing else — not the unit, not the value — so
 * they are answerable whatever else failed. Reporting them only on an otherwise-perfect answer,
 * which is what the evaluator used to do, meant a student fixed one fault per attempt and lost
 * marks to faults the block could already name.
 */
function formatFaults(raw, d) {
    const notation = d.requireExponentialNotation && !isScientificNotation(raw);
    let sigFig = d.sigFigsRequired && countSigFigs(raw) !== d.sigFigsCount;
    // Suppressed only under REAL ambiguity. `450` reads as 2 or 3 significant figures, so against a
    // requirement of 3 it may already be right and saying otherwise would be reporting a symptom of
    // the notation fault. Against a requirement of 4 no reading works, so it is simply wrong and
    // waiting to say so costs the student another attempt.
    if (sigFig && notation && d.sigFigsCount >= countSigFigs(raw) && d.sigFigsCount <= maxSigFigs(raw)) {
        sigFig = false;
    }
    return { notation, sigFig };
}
/**
 * WHICH unit the student wrote, and what their number is worth in the canonical one.
 *
 * Split out of `evaluateNumerical`'s unit branch, where resolving a unit, JUDGING it and
 * converting by it were one piece of code. Only the judgement needs a correct answer; the other
 * two are arithmetic on what was typed, and keeping them inside the verdict meant participation
 * had no canonical value and so could match no value trigger at all on a units question.
 *
 * THREE TIERS, and the order is what makes per-spelling control mean anything:
 *   1. An option's own LABEL. A row is the teacher pointing at one exact string, so it beats
 *      everything — including the canonical, which is why a row repeating the canonical is
 *      flagged in the editor rather than merely being redundant.
 *   2. The canonical and its list. Breaking `kilogram` out and marking it wrong must not drag
 *      `kilograms` along with it: the sibling is still on the canonical's line, still says
 *      "accepted", and has to keep grading that way.
 *   3. An option's other spellings. `grams` reaches the `g` row without being listed on it,
 *      which is what keeps an alternative unit from having to enumerate itself by hand.
 *
 * Tier 3 is read only where tier 2 did not answer — the `!isCanonical` guard is what enforces
 * that, so this needs no second condition of its own.
 */
function resolveSubmittedUnit(submittedUnit, submittedNumber, d) {
    const spelledBy = (label, spellings) => submittedUnit === label || !!spellings?.includes(submittedUnit ?? '');
    const namedOption = d.unitOptions.find(u => u.label === submittedUnit);
    const isCanonical = !namedOption && spelledBy(d.canonicalUnit, d.canonicalSpellings);
    const matched = namedOption ?? d.unitOptions.find(u => spelledBy(u.label, u.spellings));
    const accepted = isCanonical || matched?.role === 'correct';
    /*
     * INVERTED, because the stored conversion runs the other way. `conversionFactor` is what the
     * editor computed as canonical -> option (`opt = canon x f + o`, which is what its own
     * "= 0.5 %" preview prints), and what arrives here is a value already IN the option's unit. So
     * the canonical value is the inverse: `canon = (opt - o) / f`.
     *
     * Applying it forwards graded every correct-role alternative wrong unless its factor happened
     * to be 1 — a student answering `0.5 %` where the teacher accepted `%` for a `5 ‰` answer was
     * marked incorrect, and 20000 cm against 200 m came out as two million. Invisible for years
     * because the case it gets right is the identity: a unit and another spelling of itself.
     *
     * CLEANED, because this is our arithmetic and not the student's. They typed `212`; the
     * 100.00000000000016 that comes back is entirely ours, from a stored factor of
     * 1.7999999999999972 and a division. The same rule the accepted-range boundaries follow — clean
     * up our own arithmetic, never adjust what was submitted — and without it an exact answer with
     * no tolerance rejects a perfectly converted value.
     *
     * Only for an ACCEPTED non-canonical unit: there is no conversion toward a unit the question
     * does not take, which is why a wrong unit is terminal for the value in every mode.
     */
    const canonicalValue = !isCanonical && accepted && matched?.conversionFactor
        ? cleanComputed((submittedNumber - matched.conversionOffset) / matched.conversionFactor)
        : submittedNumber;
    return { matched, isCanonical, accepted, canonicalValue };
}
/**
 * THE BLOCK AS THE SCENARIO IN FORCE MAKES IT — the answer key and the feedback together,
 * because a row carries both.
 *
 * Graded-only, like every other answer-key resolution: a scenario selects a key, and the
 * non-graded modes have none to select.
 *
 * When no row resolves, the FIELDS fail closed — `answers: []` and `betweenAnswers: []`, which
 * is "accepts nothing", the value-range spelling of the all-neutral options a choice block
 * falls back to. The FEEDBACK stays the block's own, so the student still gets a response
 * rather than silence. Both halves say the same thing: do not grade against a key you cannot
 * justify, and do not pretend you did.
 */
export function resolveNumericalScenario(d, scenarioId) {
    /* The shared rule — see `resolveScenarioValueSetup`, which this was extracted into once four
       more value blocks were about to copy it. */
    return resolveScenarioValueSetup(d, scenarioId, NUMERICAL_VERSION_KEYS, { answers: [], betweenAnswers: [] });
}
/**
 * Targeted feedback on the ERROR BOX lives under its own prefix, so the two numbers cannot
 * accidentally answer each other's triggers. `uncertainty_exact:0.38`, `uncertainty_lt:0.2`.
 */
export const UNCERTAINTY_TRIGGER_PREFIX = 'uncertainty_';
/**
 * WHAT IS WRONG WITH THE UNCERTAINTY, most specific first.
 *
 * Shaped deliberately like the value's own verdict — teacher-authored targets and Math Error fire
 * in parallel, and the general fault is the fallback none of them reached — because a teacher who
 * has learned how one number is judged should not have to learn a second scheme for the other.
 *
 * NO NEW TAXONOMY OF PROPAGATION MISTAKES. One was scoped and dropped: the existing Math Error
 * mutators are SYNTACTIC (flip a sign, drop a paren) and so fire on any expression, whereas
 * "added instead of in quadrature" is shape-dependent and silently does nothing unless the formula
 * literally contains `sqrt(a^2+b^2)`. A taxonomy that quietly fails to fire is worse than none,
 * because the teacher believes it is covering them. The teacher authoring the wrong propagation as
 * a trigger — the same way they author a wrong unit — is reliable where that is not.
 *
 * Returns empty when the uncertainty is right.
 */
/* NO `canonicalValue` PARAMETER, and that absence is the contract rather than tidiness.
   It was read by exactly one thing — the fraction detector, `reads(uncertaintyValue /
   canonicalValue)` — which was the only arithmetic in this file joining the two submitted numbers.
   With it gone there is nothing here that could derive one from the other, and the signature is
   what keeps it that way. */
function uncertaintyVerdict(raw, unitFactor, d, resolvedVariables) {
    const { uncertaintyValue, uncertaintyEquation, feedback } = d;
    if (uncertaintyValue === undefined || !Number.isFinite(uncertaintyValue))
        return [];
    const submitted = parseFloat(raw);
    if (!Number.isFinite(submitted))
        return [];
    /**
     * CONVERTED TO CANONICAL ONCE, then compared there — the direction every other number in this
     * block is compared in, and the whole of what an alternative unit costs.
     *
     * It used to carry the BAND out to the unit the student typed instead, which existed only
     * because the comparison rounded: rounding to significant figures does not commute with
     * converting units, so the two had to meet where the digits were written. The band is a band in
     * either unit and stays canonical; what has to meet the student in their own unit is the SECOND
     * reading below, which is about digits rather than width.
     *
     * The band is still built around each CANDIDATE rather than once around the key, so a percentage
     * tolerance scales with the number it belongs to — which matters for a trap, whose number is not
     * the key.
     */
    const canonicalU = submitted / unitFactor;
    /**
     * TWO DIFFERENT QUESTIONS, and conflating them is what hid a gap here for a whole tier.
     *
     * A tolerance is the TEACHER's slack — which neighbouring uncertainties they will accept — and
     * it exists only at two significant figures, where a lattice step is small enough to mean
     * something. The student's own ROUNDING is not slack at all: they were told to write one figure,
     * so `0.28` and `0.3` are the same claim written at two precisions, and a candidate has to be
     * met where the student was asked to write it.
     *
     * The band alone was doing both jobs by accident. Every candidate in this function — the key, a
     * teacher's trap, a Math Error mutation, the relative-error slip — is an unrounded number, and
     * no student submits one. Once the band collapsed to a point at one figure, the entire
     * diagnostic tier stopped firing: a trap at `0.38` could not recognise the `0.4` it exists to
     * catch.
     *
     * BOTH SIDES, because over-writing and under-writing are the same mistake from either end: a
     * trap authored at `0.38` has to recognise the `0.4` a student submits, and a key authored at
     * `0.3` has to recognise the `0.28` they wrote without rounding. Only one of those is fixed by
     * rounding the candidate.
     *
     * The over-precise half is a SUPPRESSION, which the format fault beside it earns: `0.28` against
     * `0.3` at one figure is one mistake, not two, and saying "your uncertainty is wrong" to a
     * student whose uncertainty rounds to exactly the right number sends them to fix the wrong
     * thing. Writing it to the figures asked for is the correction, and `u-sigfig` says so.
     *
     * Rounded in the STUDENT'S unit, not the canonical one, because that is where their digits are
     * and the two do not commute — a 2 °C uncertainty is 3.6 °F, and one figure of each is a
     * different number.
     */
    const askedFigures = Math.max(1, d.uncertaintySigFigs ?? 1);
    const reads = (candidate) => {
        const band = uncertaintyBand(candidate, d);
        if (uncertaintyMatches(canonicalU, band.lo, band.hi))
            return true;
        const asWritten = roundToSigFigs(candidate * unitFactor, askedFigures);
        const asRead = roundToSigFigs(submitted, askedFigures);
        return uncertaintyMatches(asRead, asWritten, asWritten);
    };
    if (reads(uncertaintyValue))
        return [];
    const targeted = feedback.states.filter(st => {
        if (st.type !== 'targeted' || !st.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX))
            return false;
        const bare = st.trigger.slice(UNCERTAINTY_TRIGGER_PREFIX.length);
        const parsed = parseValueTrigger(bare);
        if (!parsed)
            return false;
        /*
         * An EXACT trigger names one wrong number and so needs the rounding treatment; a threshold or
         * a range is already a band and reads the plain converted value. Rounding a band would only
         * move its edges by less than the band.
         */
        return parsed.type === 'exact' ? reads(parsed.n1) : matchesValueTrigger(bare, canonicalU);
    });
    /*
     * The EXISTING seven categories, pointed at a second equation — not a new engine. Candidates
     * inside the key's own band need no filtering here the way `collectMathErrorCandidates` does it,
     * because a candidate that close already returned Correct above.
     */
    const mathErrors = [];
    if (d.mathErrorEnabled && uncertaintyEquation) {
        const candidates = generateMathErrorCandidates(uncertaintyEquation, angleModeFor(d, 'uncertaintyValue'), resolvedVariables);
        if (candidates) {
            for (const cat of MATH_ERROR_CATEGORIES) {
                if (!d.mathErrorCategories[cat])
                    continue;
                if (!candidates[cat].some(reads))
                    continue;
                /* THE ANSWER'S OWN CARD, not a second set. The toggles that enable these categories are
                   block-level and always were, so one taxonomy is what the data already said; a duplicate
                   set of cards said otherwise and doubled the panel. The CARDS are shared and the POOLS
                   are not — these candidates come from the uncertainty's equation and are compared against
                   the student's ±, while the answer's are compared against their value. Merging the pools
                   would let a mutated propagation match a value and name a mistake nobody made. */
                const st = feedback.states.find(x => x.type === 'default' && x.trigger === `math_error_${cat}`);
                if (st)
                    mathErrors.push(st);
            }
        }
    }
    /*
     * NO FALLBACK CARD. Default Incorrect is the universal catch-all and is used that way here: a
     * wrong uncertainty with nothing targeted to say about it is simply incorrect, and a teacher who
     * wants to name a particular mistake has the targeted bin.
     *
     * Empty therefore means "nothing to SAY", never "nothing wrong" — `evaluateNumerical` asks
     * `uncertaintyWrong` separately, because a wrong uncertainty must not reach Correct however
     * silent this function is about it.
     */
    return [...targeted, ...mathErrors];
}
/**
 * IS THE UNCERTAINTY WRONG — asked apart from what there is to say about it.
 *
 * `uncertaintyVerdict` used to answer both at once by always returning at least a general card, so
 * "no cards" and "no fault" were the same thing. They are not: with the catch-all collapsed into
 * Default Incorrect, a wrong uncertainty can produce an empty list, and treating that as correct
 * would mark `9.7 ± 0.9` right. This is the one-line distinction that split needs.
 */
export function uncertaintyWrong(raw, unitFactor, d) {
    const { uncertaintyValue } = d;
    if (uncertaintyValue === undefined || !Number.isFinite(uncertaintyValue))
        return false;
    const submitted = parseFloat(raw);
    if (!Number.isFinite(submitted))
        return false;
    const band = uncertaintyBand(uncertaintyValue, d);
    if (uncertaintyMatches(submitted / unitFactor, band.lo, band.hi))
        return false;
    const figs = Math.max(1, d.uncertaintySigFigs ?? 1);
    const asWritten = roundToSigFigs(uncertaintyValue * unitFactor, figs);
    return roundToSigFigs(submitted, figs) !== asWritten;
}
export function evaluateNumerical(answer, rawBlockData, 
/**
 * This attempt's variable roll, for MATH ERROR candidates only.
 *
 * Every other value on the block arrives already resolved — the caller runs each answer's
 * formula through `evaluateFormulaRaw` before building the data. The math-error equations
 * cannot be pre-resolved that way, because candidate generation works on the EXPRESSION: it
 * applies each mistake to the formula and evaluates the result, so it needs the variables at
 * the point of evaluation rather than a number computed earlier.
 *
 * Passing `{}` here, which is what this did, meant any equation containing a `{chip}` produced
 * NaN candidates and no math error could fire on a variable-driven answer. Reported: a
 * `log({test})-2` answer where a log/ln mix-up is exactly the mistake worth catching.
 */
resolvedVariables = {}, 
/** The leader variant in force, when this block follows one — see `resolveNumericalScenario`. */
scenarioId) {
    const blockData = resolveNumericalScenario(rawBlockData, scenarioId);
    const { value, unit: submittedUnit } = answer;
    const { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold, integerOnly, 
    // The canonical unit, its spellings and the option rows are read by
    // `resolveSubmittedUnit` straight off `blockData`, not here.
    unitMode, gradingMode, feedback, mathErrorEnabled, mathErrorCategories, 
    // The uncertainty KEY and its tolerance are read by `uncertaintyVerdict` off `blockData`,
    // not here — the same split `resolveSubmittedUnit` follows for the unit rows. And whether
    // one was asked for at all is `uncertaintyAsked`, which needs three fields rather than
    // this one, so the flag is deliberately not pulled out here.
    uncertaintySigFigs, } = blockData;
    const hasValue = value.trim() !== '';
    const hasUnit = !!submittedUnit;
    const requiresUnit = unitMode === 'withUnits';
    const uncertaintyRaw = (answer.uncertainty ?? '').trim();
    const hasUncertainty = uncertaintyRaw !== '';
    /*
     * READ-TIME, not only in the editor — see `uncertaintyAsked`, which is the whole rule.
     *
     * The editor greys these settings apart rather than clearing them, so a block can hold
     * `uncertaintyRequired` beside integers-only or a `between` condition. This is what makes
     * such a block, however it was authored or wherever it arrived from, still gradeable rather
     * than unanswerable.
     */
    const requiresUncertainty = uncertaintyAsked(blockData);
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(feedback.states);
    }
    /*
     * COMPLETENESS OVER EVERY FIELD THE QUESTION ASKS FOR, which is one, two or three of them.
     *
     * The pair of hand-written conditions this replaces said the same thing for value-and-unit —
     * all blank is unanswered, a proper subset blank is incomplete — but said it in a shape that
     * could not take a third field without a truth table. Enumerating what is REQUIRED and then
     * asking how much of it arrived generalises without changing either existing verdict.
     */
    const required = [
        hasValue,
        ...(requiresUnit ? [hasUnit] : []),
        ...(requiresUncertainty ? [hasUncertainty] : []),
    ];
    if (required.every(present => !present)) {
        const unanswered = feedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (required.some(present => !present)) {
        const incomplete = feedback.states.find(s => s.type === 'default' && s.trigger === 'incomplete_answer');
        return incomplete ? [incomplete] : [];
    }
    const submittedNumber = parseFloat(value);
    /*
     * Resolved for BOTH modes, which is the point of splitting it out.
     *
     * It used to happen inside the graded unit branch, below the survey return, so participation
     * had no canonical value — and `participationTargeted` therefore refused to match any trigger
     * at all on a units block, falling through to the catch-all. Resolving a unit and converting
     * by it is arithmetic on what was typed; only the VERDICT needs a correct answer.
     */
    const unitRes = resolveSubmittedUnit(submittedUnit, submittedNumber, blockData);
    /** How the answer was WRITTEN — true of the string alone, so true in every mode. */
    const faults = formatFaults(value, blockData);
    /*
     * The error box's own FORM faults — true of the strings alone, so they belong beside the value's
     * and fire in participation too. An uncertainty that is not rounded as asked, and a value whose
     * precision contradicts it, are both answerable with no correct answer anywhere in sight.
     *
     * `requireExponentialNotation` governs BOTH fields. It is a claim about how the answer is
     * written, and an answer is both numbers.
     */
    const uncertaintyNotation = requiresUncertainty && hasUncertainty
        && blockData.requireExponentialNotation && !isScientificNotation(uncertaintyRaw);
    const uncertaintyRounding = requiresUncertainty && hasUncertainty
        // `± 0` has no significant figures to round, and the correctness check already names it.
        && parseFloat(uncertaintyRaw) > 0
        && countSigFigs(uncertaintyRaw) !== (uncertaintySigFigs ?? 1);
    const advisories = [
        ...((faults.notation || uncertaintyNotation)
            ? feedback.states.filter(s => s.type === 'default' && s.trigger === 'notation_error') : []),
        ...(faults.sigFig ? feedback.states.filter(s => s.type === 'default' && s.trigger === 'sigfig_error') : []),
        ...(uncertaintyRounding
            ? feedback.states.filter(s => s.type === 'default' && s.trigger === 'uncertainty_sigfig_error') : []),
    ];
    /** The unit fault, most specific first: this unit's own card, then the general one. Default
     *  Incorrect is deliberately not a fallback outside graded — there is nothing to be wrong about. */
    const unitFault = (requiresUnit && !unitRes.accepted)
        ? [
            ...(unitRes.matched
                ? feedback.states.filter(s => s.type === 'targeted' && s.trigger === `unit_${unitRes.matched.id}`)
                : []),
            ...feedback.states.filter(s => s.type === 'default' && s.trigger === 'unit_error'),
        ].slice(0, 1)
        : [];
    if (gradingMode === 'survey') {
        /*
         * PARTICIPATION DIAGNOSES FORM, and only form.
         *
         * Everything here is answerable without a correct answer: whether the string is in
         * scientific notation, how many significant figures it carries, and whether the unit is one
         * the question takes. The teacher opted into each by setting the requirement, and the
         * requirement belongs to the QUESTION ("to 4 sig figs, in joules") rather than to the
         * grading, so switching off the verdict does not switch off the ask.
         *
         * Math errors are deliberately absent: their candidates are generated by mutating the
         * CORRECT formula, so they diagnose a relation to a reference the mode does not yet have.
         * They join once Set Answer makes the authored answer a reference — see the participation
         * plan.
         *
         * A wrong unit stays terminal FOR THE VALUE, exactly as in graded: `500 g` against `400 m`
         * is not a wrong number, it is an incomparable one, and no conversion exists to compare it.
         * The format faults ride alongside, because they were never downstream of the unit.
         */
        const rangeForPreset = {
            conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold, integerOnly,
        };
        /*
         * SET ANSWER is the accepted band, targetable like any other condition once the mode stops
         * grading — "the correct state minus the verdict". It is deliberately NOT terminal: the use
         * it exists for is "you are probably right, but let us look at the reasoning", which needs
         * the card to fire AND a pathway to route.
         *
         * `isWithinAcceptedRange` already reads every authored answer, so a block with a LIST of
         * accepted values needs no special case: one card means "any value this question accepts",
         * which is what the Correct card already summarises.
         */
        /*
         * `!requiresUnit ||` — a block that takes no units has no unit to accept, and
         * `resolveSubmittedUnit` reports `accepted: false` for it because nothing matched. Without
         * this the SIMPLEST setup of all, a plain number with a tolerance, matched no targeted state
         * at all: the guard is about a wrong unit being incomparable, and there is no unit here.
         */
        const valueMatches = (!requiresUnit || unitRes.accepted) && Number.isFinite(submittedNumber)
            ? feedback.states.filter(st => st.type !== 'targeted' ? false
                : st.trigger === SET_ANSWER_TRIGGER
                    ? isWithinAcceptedRange(unitRes.canonicalValue, rangeForPreset)
                    : matchesValueTrigger(st.trigger, unitRes.canonicalValue))
            : [];
        /* No key here, so no range check to pass — format alone is what the uncertainty can earn its
           say with, and agreement is answerable on the two strings once it has. */
        const surveyAgreement = (requiresUncertainty && hasUncertainty && hasValue
            && !uncertaintyRounding && precisionDisagrees(value, uncertaintyRaw))
            ? feedback.states.filter(x => x.type === 'default' && x.trigger === 'agreement_error')
            : [];
        return participationOutcome(feedback.states, 'survey', [...valueMatches, ...unitFault, ...advisories, ...surveyAgreement]);
    }
    if (!Number.isFinite(submittedNumber)) {
        const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
        return incorrect ? [incorrect] : [];
    }
    /*
     * The unit VERDICT, which is graded's alone — see `resolveSubmittedUnit` for the resolution
     * and the conversion, which both modes share.
     *
     * Terminal for the value: `500 g` against `400 m` is not a wrong number, it is an incomparable
     * one, and no conversion exists. The format faults ARE reported, because they were never
     * downstream of the unit.
     */
    if (requiresUnit && !unitRes.accepted) {
        // Most specific first: this unit's own feedback, then the general "not a unit this question
        // accepts", then Default Incorrect for a block saved before that state existed.
        const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
        const verdict = unitFault[0] ?? incorrect;
        return verdict ? [verdict, ...advisories] : advisories;
    }
    const canonicalValue = unitRes.canonicalValue;
    /*
     * THE UNCERTAINTY CONVERTS BY THE FACTOR ALONE — never the offset.
     *
     * A difference is invariant under an offset: a 2 °C uncertainty is 3.6 °F, not 39.6 °F. The value
     * beside it may legitimately need the offset (`resolveSubmittedUnit` applies it, governed by the
     * block's `tempConvMode`), but an uncertainty is a difference whatever the value is, so it never
     * does. Reusing the value's conversion path here would pass every unit whose offset is zero,
     * which is all of them but temperature.
     */
    const unitFactor = (!unitRes.isCanonical && unitRes.accepted && unitRes.matched?.conversionFactor)
        ? unitRes.matched.conversionFactor
        : 1;
    /*
     * Graded-only, unlike the form faults above: this one needs a correct answer, and participation
     * has none. Same reason Math Error sits out of that mode.
     */
    const uncertaintyFault = (requiresUncertainty && hasUncertainty)
        ? uncertaintyVerdict(uncertaintyRaw, unitFactor, blockData, resolvedVariables)
        : [];
    /*
     * WRONG, AND WHAT THERE IS TO SAY ABOUT IT, ARE DIFFERENT QUESTIONS.
     *
     * They used to be one, because the verdict always ended in a general card. With that card gone
     * an empty list means only that nothing targeted matched, so correctness is asked separately —
     * otherwise `9.7 ± 0.9` reads as Correct.
     */
    /* NOT gated on the format fault. A mis-rounded uncertainty is still a number, and `0.21` where
       `0.3` was wanted is wrong twice over — once for the figures, once because it rounds to 0.2.
       Gate 2 kills the AGREEMENT check below, because an uncertainty with no settled last place
       gives the value nothing to agree with; it does not kill the uncertainty's own value check. */
    const uncertaintyIsWrong = requiresUncertainty && hasUncertainty
        && uncertaintyWrong(uncertaintyRaw, unitFactor, blockData);
    /*
     * AGREEMENT IS A SELF-CONSISTENCY RULE ON THE STUDENT'S OWN TWO NUMBERS.
     *
     * `9.81` and `± 0.2` disagree on their own terms, whatever the key says — the check compares
     * what the student wrote against what the student wrote, and derives nothing from the answer
     * key at all.
     *
     * It used to wait for the uncertainty to be CORRECT, on the reasoning that a wrong number has no
     * standing to imply a precision. That belongs to the earlier model: it made two independent
     * faults arrive one round trip apart — fix the ± and only then hear about the digits — and it
     * put a value check inside a format check, which is the coupling the parallel verdicts exist to
     * avoid.
     *
     * The FORMAT gate stays. An uncertainty written to the wrong number of figures has no settled
     * last significant place, so there is nothing for the value to agree with. That is gate 2
     * killing gate 3, which is the evaluation order itself rather than an exception to it.
     */
    const agreementFault = (requiresUncertainty && hasUncertainty && hasValue
        && !uncertaintyRounding
        && precisionDisagrees(value, uncertaintyRaw))
        ? feedback.states.filter(x => x.type === 'default' && x.trigger === 'agreement_error')
        : [];
    const rangeConfig = { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold, integerOnly };
    if (isWithinAcceptedRange(canonicalValue, rangeConfig)) {
        // A format fault IS the verdict here: it is the only thing wrong, and a teacher who required
        // the notation meant the answer to carry it. Notation leads, because the sig-fig count of a
        // non-normalized number is ambiguous and `formatFaults` has already decided whether that
        // ambiguity could go the student's way.
        // A wrong uncertainty is not Correct, however right the value is — it is the other half of the
        // answer, and it leads because it is the substantive fault where the advisories are about form.
        if (uncertaintyIsWrong || uncertaintyFault.length > 0 || agreementFault.length > 0
            || advisories.length > 0) {
            /* The catch-all stands in only where nothing SUBSTANTIVE was said. Advisories do not count:
               they are about form, so "your uncertainty is written to the wrong number of figures" must
               not be the whole of what a student hears when the number is also wrong. */
            const fallback = uncertaintyIsWrong && uncertaintyFault.length === 0
                ? feedback.states.filter(s => s.type === 'default' && s.trigger === 'incorrect')
                : [];
            return [...fallback, ...uncertaintyFault, ...agreementFault, ...advisories];
        }
        const correct = feedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    // Not in range: fire all matching value-feedback states (parallel — multiple can fire at once).
    const matched = feedback.states.filter(s => s.type === 'targeted' && matchesValueTrigger(s.trigger, canonicalValue));
    // Math Error — checked here (only once the value is already confirmed out of range), so
    // it can never override a genuinely correct answer, and fires in parallel with any
    // manually-authored targeted value-feedback matching the same number (same "multiple
    // states can fire together" convention as `matched` above; no new combo-state logic).
    const mathErrorMatches = [];
    if (mathErrorEnabled && (conditionType === 'exact' || conditionType === 'between')) {
        /** Each equation WITH the answer it came from, so its own mode survives the filter. */
        const pairs = conditionType === 'exact'
            ? answers.map(a => ({ eq: a.mathErrorEquation, fieldId: `${a.id}.value` }))
            : betweenAnswers.flatMap(a => [
                { eq: a.mathErrorEquationLow, fieldId: a.id }, { eq: a.mathErrorEquationHigh, fieldId: a.id }
            ]);
        const kept = pairs.filter((p) => !!p.eq);
        const equations = kept.map(p => p.eq);
        if (equations.length > 0) {
            const candidates = collectMathErrorCandidates(equations, i => angleModeFor(blockData, kept[i].fieldId), resolvedVariables, rangeConfig);
            for (const cat of MATH_ERROR_CATEGORIES) {
                if (!mathErrorCategories[cat])
                    continue;
                if (candidates[cat].some(v => Math.abs(canonicalValue - v) <= candidateHalfWidth(v, rangeConfig, value))) {
                    const state = feedback.states.find(s => s.type === 'default' && s.trigger === `math_error_${cat}`);
                    if (state)
                        mathErrorMatches.push(state);
                }
            }
        }
    }
    /* DEDUPED, because the seven categories are now one set of cards fed by two equations: flip a
       sign in the answer and in the propagation and the same state would otherwise be listed twice. */
    const dedupe = (states) => {
        const seen = new Set();
        return states.filter(x => !seen.has(x.id) && seen.add(x.id));
    };
    if (matched.length > 0 || mathErrorMatches.length > 0) {
        return dedupe([...matched, ...mathErrorMatches, ...uncertaintyFault, ...agreementFault,
            ...advisories]);
    }
    const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return dedupe(incorrect
        ? [incorrect, ...uncertaintyFault, ...agreementFault, ...advisories]
        : [...uncertaintyFault, ...agreementFault, ...advisories]);
}
/**
 * Whether Default Incorrect (for the value dimension) is genuinely unreachable — every
 * point on the real line is covered by either the accepted condition or some value feedback.
 * Handles the common realistic case (a chain of range/threshold feedback states tiling the
 * space around a correct zone) via an interval sweep, not just a single flanking pair.
 */
export function isValueDefaultIncorrectUnreachable(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, numericalTriggerSpan);
    return coverageSpansDomain(merged, -Infinity, Infinity);
}
/**
 * The value-dimension gaps that actually reach Default Incorrect — the real line's
 * complement of the accepted range(s) plus any value feedback coverage. Each gap is
 * reported as-is (possibly ±Infinity on one side); formatting/wording is the caller's
 * job. Empty array means Default Incorrect is unreachable via the value dimension
 * (matches isValueDefaultIncorrectUnreachable — this is effectively its inverse).
 */
export function computeDefaultIncorrectGaps(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, numericalTriggerSpan);
    const gaps = [];
    if (merged.length === 0)
        return [{ lo: -Infinity, hi: Infinity, loInclusive: true, hiInclusive: true }];
    const first = merged[0];
    // A gap's boundary is the mirror of the span beside it: coverage that stops OPEN at 5 leaves a gap
    // CLOSED at 5, because 5 itself is uncovered. That is how a single-point gap arises, and
    // representing it is the difference between "Default Incorrect is unreachable" and "it is reached
    // by exactly one value".
    if (first.lo !== -Infinity) {
        gaps.push({ lo: -Infinity, hi: first.lo, loInclusive: true, hiInclusive: !first.loInclusive });
    }
    for (let i = 1; i < merged.length; i++) {
        const prev = merged[i - 1];
        const cur = merged[i];
        if (cur.lo > prev.hi || (cur.lo === prev.hi && !prev.hiInclusive && !cur.loInclusive)) {
            gaps.push({ lo: prev.hi, hi: cur.lo, loInclusive: !prev.hiInclusive, hiInclusive: !cur.loInclusive });
        }
    }
    const last = merged[merged.length - 1];
    if (last.hi !== Infinity) {
        gaps.push({ lo: last.hi, hi: Infinity, loInclusive: !last.hiInclusive, hiInclusive: true });
    }
    return gaps;
}
export function findOverlappingFeedbackPairs(feedbackTriggers) {
    const parsed = feedbackTriggers
        .map(trigger => {
        const p = parseValueTrigger(trigger);
        if (!p || p.type === 'exact')
            return null;
        if (p.type === 'lt')
            return { trigger, lo: -Infinity, hi: p.n1, loInclusive: true, hiInclusive: false };
        if (p.type === 'lte')
            return { trigger, lo: -Infinity, hi: p.n1, loInclusive: true, hiInclusive: true };
        if (p.type === 'gt')
            return { trigger, lo: p.n1, hi: Infinity, loInclusive: false, hiInclusive: true };
        if (p.type === 'gte')
            return { trigger, lo: p.n1, hi: Infinity, loInclusive: true, hiInclusive: true };
        return { trigger, lo: Math.min(p.n1, p.n2), hi: Math.max(p.n1, p.n2), loInclusive: true, hiInclusive: true };
    })
        .filter((x) => x !== null);
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (rangesOverlap(a, b))
                pairs.push([a.trigger, b.trigger]);
        }
    }
    return pairs;
}
/**
 * Whether a value-feedback state's *entire* condition now falls inside the accepted
 * range — the value-side equivalent of unit feedback whose target unit got promoted to
 * correct. This is a different, narrower question than "does it overlap Correct":
 * feedback like `gt:60` that partially overlaps a widened accepted range (say 55–75) is
 * still meaningfully reachable for values above 75, just narrower than authored — that's
 * the lower-stakes "feedback vs. Correct" overlap case, not full subsumption. Only
 * feedback whose whole domain is now covered — e.g. `exact:42` once 42 falls inside the
 * accepted window, or `range`/`gt`/`lt` feedback entirely swallowed by a widened
 * condition — is truly dead and worth hiding (never wiped — same "suppress, don't
 * destroy" rule as everywhere else; a teacher may narrow the accepted range back later
 * and the feedback should just reappear).
 */
export function isValueFeedbackFullySubsumed(trigger, acceptedRanges) {
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return isIntervalFullyCovered(target, acceptedRanges);
}
/**
 * Whether a value-feedback trigger's target interval intersects the accepted Correct
 * range at all — a strictly weaker condition than isValueFeedbackFullySubsumed (which
 * requires the *entire* target to be covered). Meant for feedback that's only partially
 * eaten into by Correct (e.g. `gte:11` when Correct accepts up to 11) — still reachable
 * overall, but some of its authored range will never actually be evaluated against it,
 * since a value landing in the accepted range is claimed by Correct first (see
 * evaluateNumerical's correct-first-terminal ordering).
 *
 * Boundary strictness matters here, not just as a nicety: Correct's own accepted edge is
 * always inclusive (exact/between answers' boundaries are inclusive — see
 * isWithinAcceptedRange's generic fallback), so `lt:9` against an accepted range starting
 * at 9 does NOT overlap (lt excludes 9 itself, and that's the only point in question),
 * while `lte:9` against the same range DOES (lte includes 9, which Correct also claims).
 * Getting this wrong either warns on well-formed complementary triggers (a false alarm
 * that trains teachers to ignore the warning) or misses a real double-covered point.
 *
 * Exact-value feedback has no meaningful "partial" case here — a point either falls
 * fully inside Correct (subsumed) or fully outside (no overlap) — so this always
 * returns false for exact triggers; call isValueFeedbackFullySubsumed for that case.
 */
export function doesValueFeedbackOverlapAccepted(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed || parsed.type === 'exact')
        return false;
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return acceptedRanges.some(r => {
        if (target.hi < r.lo || r.hi < target.lo)
            return false;
        // BOTH sides' boundaries count now. The accepted range used to be assumed closed, so
        // Correct = "<= 10" and a trigger of "> 10" — perfect complements sharing no point — were
        // reported as overlapping.
        if (target.hi === r.lo)
            return target.hiInclusive && r.loInclusive;
        if (r.hi === target.lo)
            return r.hiInclusive && target.loInclusive;
        return true;
    });
}
/**
 * Exact-value feedback triggers whose point falls inside the given Default Incorrect
 * gap — targeted feedback that "carves out" a single value from what would otherwise
 * read as a plain incorrect range. Doesn't reshape the gap's own boundaries (a single
 * point can't close a real gap in a continuous domain, per computeDefaultIncorrectGaps'
 * own exclusion of exact triggers) — this is purely for annotating the gap's display
 * text with "except N" so the teacher can see that value is actually handled elsewhere.
 */
export function exactFeedbackPointsInGap(gap, feedbackTriggers) {
    const points = [];
    for (const trigger of feedbackTriggers) {
        const parsed = parseValueTrigger(trigger);
        if (!parsed || parsed.type !== 'exact')
            continue;
        // The gap's OWN boundary rule decides, the same standing convention every other overlap check
        // in this file follows. Comparing closed regardless read a point sitting exactly on an open
        // edge as being inside — so a correct answer of 400 with targeted feedback also at 400 wrote
        // "< 400 (except 400)", excluding from a gap the value that defines its edge and is not in it.
        const aboveLo = gap.loInclusive ? parsed.n1 >= gap.lo : parsed.n1 > gap.lo;
        const belowHi = gap.hiInclusive ? parsed.n1 <= gap.hi : parsed.n1 < gap.hi;
        if (aboveLo && belowHi)
            points.push(parsed.n1);
    }
    return [...new Set(points)].sort((a, b) => a - b);
}
// ─── Math Error auto-detection ─────────────────────────────────────────────────
// Given the equation a teacher expects a student to compute (e.g. "5+(3-4)^2"), generates
// the numeric results of 7 common calculation mistakes against it, so targeted feedback
// for each doesn't have to be hand-computed and re-computed every time the equation
// changes. See MATH_ERROR_CATEGORIES for the category list.
/** Merges resolved variable values into buildFormulaScope's function-override scope (see
 * formula-scope.ts — the one shared scope every formula input in the app evaluates through).
 * variableValues is the block's current preview roll at authoring time, or a specific
 * attempt's resolved roll at grading time (once a grading runtime exists to supply one). */
function buildScope(angleMode, variableValues) {
    const scope = buildFormulaScope(angleMode);
    for (const [k, v] of Object.entries(variableValues)) {
        const n = parseFloat(v);
        if (isNaN(n))
            continue;
        // Both forms, as `buildScopeWithValues` does: a variable under its own name, an alias
        // under its sanitised key. One map serves either without the caller knowing which it holds.
        scope[k] = n;
        scope[formulaRefKey(k)] = n;
    }
    return scope;
}
function safeEvalNode(node, scope) {
    try {
        const v = node.evaluate(scope);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
function safeEvalString(s, scope) {
    try {
        const v = parse(s).evaluate(scope);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
function dedupeValues(values) {
    const out = [];
    for (const v of values)
        if (!out.some(o => Math.abs(o - v) < EPS(v)))
            out.push(v);
    return out;
}
function findParenPairs(s) {
    const stack = [];
    const pairs = [];
    for (let i = 0; i < s.length; i++) {
        if (s[i] === '(')
            stack.push(i);
        else if (s[i] === ')') {
            const open = stack.pop();
            if (open !== undefined)
                pairs.push([open, i]);
        }
    }
    return pairs;
}
/** Inserts an explicit '*' at every implicit-multiplication boundary (digit/')' immediately
 * followed by '(', or ')' immediately followed by digit/'(') so the text-level mutations
 * below (paren splice, left-to-right token flatten) never accidentally fuse adjacent
 * tokens once characters are removed — e.g. "2(3+4)" → "2*(3+4)" before any splicing. */
function normalizeImplicitMultiplication(s) {
    let prev;
    do {
        prev = s;
        s = s.replace(/([0-9)])\s*\(/g, '$1*(');
        s = s.replace(/\)\s*([0-9(])/g, ')*$1');
    } while (s !== prev);
    return s;
}
/** Flip every +/- binary operator and every unary minus, one at a time, evaluating each
 * resulting variant. E.g. "5+(3-4)^2" → flip inner '-': "5-(3-4)^2"=4; flip outer '+':
 * "5+(3+4)^2"=54. */
function mutateSignFlip(root, scope) {
    const targets = [];
    root.traverse(n => {
        if (n.type === 'OperatorNode') {
            const fn = n.fn;
            if (fn === 'add' || fn === 'subtract' || fn === 'unaryMinus')
                targets.push(n);
        }
    });
    const results = [];
    for (const target of targets) {
        const variant = root.transform(n => {
            if (n !== target)
                return n;
            const on = n;
            if (on.fn === 'add')
                return new OperatorNode('-', 'subtract', on.args);
            if (on.fn === 'subtract')
                return new OperatorNode('+', 'add', on.args);
            return on.args[0]; // unaryMinus — drop the negation
        });
        const v = safeEvalNode(variant, scope);
        if (v !== null)
            results.push(v);
    }
    return results;
}
/** Remove one parenthesized group from the source text at a time and let the parser
 * re-derive grouping under standard precedence — this also covers "failed to distribute"
 * (removing the parens from "2*(3+4)" naturally gives "2*3+4", the distribute mistake).
 * Redundant parens that don't change the result are filtered by the differs-check. */
function mutateDroppedParens(normalizedSource, scope, correctValue) {
    const results = [];
    for (const [open, close] of findParenPairs(normalizedSource)) {
        const variantText = normalizedSource.slice(0, open) + normalizedSource.slice(open + 1, close) + normalizedSource.slice(close + 1);
        const v = safeEvalString(variantText, scope);
        if (v !== null && Math.abs(v - correctValue) >= EPS(correctValue))
            results.push(v);
    }
    return results;
}
/** Replace every power operator with ordinary multiplication of the same operands
 * (a^b → a*b) — the "read the exponent as multiplication" mistake. */
function mutateExponentMisread(root, scope) {
    const targets = [];
    root.traverse(n => {
        if (n.type === 'OperatorNode' && n.fn === 'pow')
            targets.push(n);
    });
    const results = [];
    for (const target of targets) {
        const variant = root.transform(n => n !== target ? n : new OperatorNode('*', 'multiply', n.args));
        const v = safeEvalNode(variant, scope);
        if (v !== null)
            results.push(v);
    }
    return results;
}
/** Reverse the two operands of every non-commutative binary operator (subtract, divide) —
 * the "substituted values in the wrong order" mistake. */
function mutateSwappedOperands(root, scope) {
    const targets = [];
    root.traverse(n => {
        if (n.type === 'OperatorNode') {
            const fn = n.fn;
            if (fn === 'subtract' || fn === 'divide')
                targets.push(n);
        }
    });
    const results = [];
    for (const target of targets) {
        const variant = root.transform(n => {
            if (n !== target)
                return n;
            const on = n;
            return new OperatorNode(on.op, on.fn, [on.args[1], on.args[0]]);
        });
        const v = safeEvalNode(variant, scope);
        if (v !== null)
            results.push(v);
    }
    return results;
}
/** Evaluate the token stream strictly left-to-right, ignoring both parentheses and
 * standard operator precedence entirely (the "typed into a basic calculator" mistake).
 * Operates on the normalized (implicit-multiplication-explicit) source with all
 * parentheses stripped. Handles both number literals and variable-symbol tokens (resolved
 * via variableValues) — a hand-rolled tokenizer rather than mathjs's real parser (which
 * always understands standard precedence), so it has to recognize identifiers itself
 * rather than relying on evaluate(scope) to resolve them. Returns null if the source
 * doesn't tokenize cleanly, or references a variable missing from variableValues. */
function mutateIgnoredPrecedence(normalizedSource, variableValues) {
    const stripped = normalizedSource.replace(/[()]/g, '');
    const tokens = stripped.match(/[0-9.]+(?:[eE][+-]?[0-9]+)?|[a-zA-Z_]\w*|[+\-*/^]/g);
    if (!tokens || tokens.length === 0)
        return null;
    const values = [];
    const ops = [];
    let i = 0;
    let pendingSign = 1;
    let expectNumber = true;
    while (i < tokens.length) {
        const tok = tokens[i];
        if (tok === '+' || tok === '-') {
            if (expectNumber) {
                if (tok === '-')
                    pendingSign *= -1;
                i++;
                continue;
            }
            ops.push(tok);
            expectNumber = true;
            i++;
            continue;
        }
        if (tok === '*' || tok === '/' || tok === '^') {
            ops.push(tok);
            expectNumber = true;
            i++;
            continue;
        }
        const isIdentifier = /^[a-zA-Z_]/.test(tok);
        const n = isIdentifier ? parseFloat(variableValues[tok] ?? '') : parseFloat(tok);
        if (!Number.isFinite(n))
            return null;
        values.push(n * pendingSign);
        pendingSign = 1;
        expectNumber = false;
        i++;
    }
    if (values.length === 0)
        return null;
    let acc = values[0];
    for (let k = 0; k < ops.length; k++) {
        const op = ops[k];
        const next = values[k + 1];
        if (next === undefined)
            return null;
        if (op === '+')
            acc = acc + next;
        else if (op === '-')
            acc = acc - next;
        else if (op === '*')
            acc = acc * next;
        else if (op === '/')
            acc = acc / next;
        else if (op === '^')
            acc = Math.pow(acc, next);
    }
    return Number.isFinite(acc) ? acc : null;
}
/** If the equation calls any angle-dependent function, evaluate the whole equation once
 * with angleMode flipped from the block's actual setting — a single global re-evaluation
 * (not a per-node substitution), since a calculator's angle mode applies to the whole
 * computation, not one step of it. Equations with no angle-dependent call produce no
 * candidate (the differs-check alone would already filter an identical result, but the
 * containment check skips the pointless evaluation). */
function mutateWrongAngleMode(root, angleMode, variableValues, correctValue) {
    let hasAngleFn = false;
    root.traverse(n => {
        if (n.type === 'FunctionNode' && ANGLE_FUNCTION_NAMES.has(n.fn.name))
            hasAngleFn = true;
    });
    if (!hasAngleFn)
        return [];
    const flipped = angleMode === 'degrees' ? 'radians' : 'degrees';
    const v = safeEvalNode(root, buildScope(flipped, variableValues));
    if (v === null || Math.abs(v - correctValue) < EPS(correctValue))
        return [];
    return [v];
}
/** Swap a single-argument log(x) for ln(x), or vice versa, one call at a time — the
 * calculator-convention log(x)=log10(x) vs. mathematician's-convention ln(x) mix-up. The
 * 2-argument log(x, base) form is a deliberate custom base and is left untouched. */
function mutateLogLnConfusion(root, scope) {
    const targets = [];
    root.traverse(n => {
        if (n.type === 'FunctionNode') {
            const fn = n;
            if ((fn.fn.name === 'log' || fn.fn.name === 'ln') && fn.args.length === 1)
                targets.push(n);
        }
    });
    const results = [];
    for (const target of targets) {
        const variant = root.transform(n => {
            if (n !== target)
                return n;
            const fn = n;
            const newName = fn.fn.name === 'log' ? 'ln' : 'log';
            return new FunctionNode(new SymbolNode(newName), fn.args);
        });
        const v = safeEvalNode(variant, scope);
        if (v !== null)
            results.push(v);
    }
    return results;
}
/**
 * Given one equation string (plain numbers/operators, or a {variable}-chip formula), the
 * block's angleMode, and resolved variable values (the current preview roll at authoring
 * time, or a specific attempt's roll at grading time), returns all 7 categories' raw
 * candidate sets (deduped within each category, and with any candidate matching the
 * equation's own correct value filtered out — a mutation that happens not to change the
 * result isn't really a mistake). Returns null if the string doesn't parse to a numeric
 * expression at all (empty, malformed, or references a variable missing from
 * variableValues).
 */
export function generateMathErrorCandidates(equation, angleMode, variableValues) {
    // The shared rewrite — see `preprocessFormula`. This knew only `{chips}`, so an answer
    // quoting an earlier one produced no candidates at all rather than candidates it could not
    // judge, which is a different and quieter failure.
    const stripped = preprocessFormula(equation.trim());
    if (!stripped)
        return null;
    const scope = buildScope(angleMode, variableValues);
    let root;
    try {
        root = parse(stripped);
    }
    catch {
        return null;
    }
    const correctValue = safeEvalNode(root, scope);
    if (correctValue === null)
        return null;
    const normalized = normalizeImplicitMultiplication(stripped);
    const ignoredPrecedenceValue = mutateIgnoredPrecedence(normalized, variableValues);
    const raw = {
        sign_flip: mutateSignFlip(root, scope),
        dropped_parens: mutateDroppedParens(normalized, scope, correctValue),
        exponent_misread: mutateExponentMisread(root, scope),
        swapped_operands: mutateSwappedOperands(root, scope),
        ignored_precedence: ignoredPrecedenceValue !== null ? [ignoredPrecedenceValue] : [],
        wrong_angle_mode: mutateWrongAngleMode(root, angleMode, variableValues, correctValue),
        log_ln_confusion: mutateLogLnConfusion(root, scope),
    };
    const out = {};
    for (const cat of MATH_ERROR_CATEGORIES) {
        out[cat] = dedupeValues(raw[cat].filter(v => Math.abs(v - correctValue) >= EPS(correctValue)));
    }
    return out;
}
/**
 * Unions candidate sets across every equation-bearing slot supplied (every accepted exact
 * answer's equation, or every between answer's low/high equation), dedupes per category,
 * and discards any candidate inside the accepted range/tolerance — it would grade Correct
 * anyway, per evaluateNumerical's correct-first-terminal ordering, so this composes with
 * the existing isWithinAcceptedRange rather than needing new "is this actually wrong" logic.
 */
export function collectMathErrorCandidates(equations, 
/**
 * One mode, or a LOOKUP by the equation's index.
 *
 * Each equation here belongs to a different answer field, and a field carries its own angle
 * mode — so one mode cannot speak for the list. The scalar form stays for callers, and every
 * test, that genuinely have one.
 */
angleMode, variableValues, acceptedRangeConfig) {
    const modeOf = typeof angleMode === 'function' ? angleMode : () => angleMode;
    const merged = {};
    for (const cat of MATH_ERROR_CATEGORIES)
        merged[cat] = [];
    for (const [i, eq] of equations.entries()) {
        const candidates = generateMathErrorCandidates(eq, modeOf(i), variableValues);
        if (!candidates)
            continue;
        for (const cat of MATH_ERROR_CATEGORIES)
            merged[cat].push(...candidates[cat]);
    }
    const out = {};
    for (const cat of MATH_ERROR_CATEGORIES) {
        out[cat] = dedupeValues(merged[cat]).filter(v => !isWithinAcceptedRange(v, acceptedRangeConfig));
    }
    return out;
}
export { valuesMatchWithinEpsilon };
/**
 * The fields a Numerical version owns — §3.2 of `docs/question-versions.md`, and the split that
 * needed the most care of any block.
 *
 * VARIES, because it is the answer: the accepted values and the ranges, the error model, and
 * `conditionType`, which travels with `answers` because either without the other is meaningless.
 *
 * SHARED, and this is the load-bearing half:
 *
 *   - UNITS (`unitMode`, `canonicalUnit`, `canonicalSpellings`, `unitOptions`). The unit options
 *     are the option list for the unit half of the answer, so §3.3's argument applies to them
 *     unchanged — the same reason MCQ's option TEXT is shared while only the roles vary. A bank
 *     whose variants want different units is two questions, not one.
 *   - FORMAT GATES (`sigFigsRequired`, `sigFigsCount`, `requireExponentialNotation`). A sig-fig
 *     or notation gate constrains the FORM a valid answer may take, never which answer is right.
 *   - `mathErrorEnabled`/`mathErrorCategories` and `angleMode`, for the same reason: they say how
 *     an answer is interpreted, not what it should be.
 *
 * That `conditionType` varies does let v1 ask for an exact value while v2 accepts a range, which
 * makes the variants unequal as items. §3.4 leaves that to the teacher, as it does everywhere
 * else a bank can be made uneven.
 */
export const NUMERICAL_VERSION_KEYS = [
    'conditionType', 'answers', 'toleranceMode', 'toleranceSymmetry', 'betweenAnswers', 'threshold',
];
export function applyNumericalVersion(data, versionId) {
    return applyFieldVersion(data, versionId, NUMERICAL_VERSION_KEYS);
}
export function writeNumericalVersion(current, versionId, next) {
    return writeFieldVersion(current, versionId, next, NUMERICAL_VERSION_KEYS);
}
export function addNumericalVersion(data, fromVersionId, newId) {
    return addFieldVersion(data, fromVersionId, newId, NUMERICAL_VERSION_KEYS);
}
export function removeNumericalVersion(data, versionId) {
    return removeFieldVersion(data, versionId, NUMERICAL_VERSION_KEYS);
}
//# sourceMappingURL=numerical.js.map